/**
 * ChartBlock — the data contract for chart visualization components.
 *
 * This is a pure TypeScript file with zero framework imports.
 * Backend tool schemas (Zod) should mirror these types.
 */

export interface ChartDataPoint {
  label: string;
  value: number;
  series?: string;
  color?: string;
}

export interface ChartBlock {
  title: string;
  chart_type: 'bar' | 'line' | 'pie' | 'donut' | 'stacked_bar';
  data: ChartDataPoint[];
  x_label?: string;
  y_label?: string;
  value_format?: 'number' | 'currency' | 'percent';
  show_legend?: boolean;
  orientation?: 'horizontal' | 'vertical';
}

const VALID_CHART_TYPES = new Set(['bar', 'line', 'pie', 'donut', 'stacked_bar']);

export function isValidChartBlock(data: unknown): data is ChartBlock {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  if (typeof d.title !== 'string') return false;
  if (!VALID_CHART_TYPES.has(d.chart_type as string)) return false;
  if (!Array.isArray(d.data) || d.data.length === 0) return false;
  return d.data.every(
    (p: unknown) =>
      p &&
      typeof p === 'object' &&
      typeof (p as Record<string, unknown>).label === 'string' &&
      typeof (p as Record<string, unknown>).value === 'number'
  );
}
